/**
 * MediaCatalog — grid of media summaries fetched from /api/media.
 *
 * Renders poster, title, duration, and a status badge. Only `ready` items are
 * clickable (pending/transcoding/failed are informational). Handles loading,
 * error, and empty states explicitly.
 */
import { useEffect, useRef, useState } from 'react';
import type { MediaSummary, MediaStatus } from '@syncreel/shared';
import { getMediaList, deleteMedia, getJobs } from '../api/client';
import { useSessionStore } from '../store/session';
import { Badge, type BadgeVariant } from './ui/Badge';
import { Button } from './ui/Button';
import { Spinner } from './ui/Spinner';
import { useToast } from './ui/Toast';
import styles from './MediaCatalog.module.css';

export interface MediaCatalogProps {
  /** Called when a ready media item is activated. */
  onSelect: (media: MediaSummary) => void;
  /** Bump to force a reload (e.g. after a file was added from the library browser). */
  refreshKey?: number;
  /** Id of the currently selected item (gets a highlight ring). */
  selectedId?: string | undefined;
}

/** Format seconds as H:MM:SS or M:SS. */
export function formatDuration(totalSec: number): string {
  if (!Number.isFinite(totalSec) || totalSec < 0) return '--:--';
  const s = Math.floor(totalSec % 60);
  const m = Math.floor((totalSec / 60) % 60);
  const h = Math.floor(totalSec / 3600);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** Map a media status to a badge variant + label, with optional progress. */
function statusBadge(
  status: MediaStatus,
  progress?: number,
): { variant: BadgeVariant; label: string } {
  switch (status) {
    case 'ready':
      return { variant: 'ok', label: 'Ready' };
    case 'transcoding': {
      if (progress !== undefined && progress > 0) {
        const pct = Math.round(progress * 100);
        return { variant: 'info', label: `Transcoding ${pct}%` };
      }
      return { variant: 'info', label: 'Transcoding…' };
    }
    case 'pending':
      return { variant: 'neutral', label: 'Pending' };
    case 'failed':
      return { variant: 'error', label: 'Failed' };
  }
}

/** Render the media catalog grid with async state handling. */
export function MediaCatalog({ onSelect, refreshKey = 0, selectedId }: MediaCatalogProps) {
  const toast = useToast();
  const adminToken = useSessionStore((s) => s.adminToken);
  const [items, setItems] = useState<MediaSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<Record<string, number>>({});
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Silent reload tick: while anything is still pending/transcoding, refetch so
  // the card flips to Ready without a manual page refresh.
  const [pollTick, setPollTick] = useState<number>(0);
  const hasActive = items.some((m) => m.status === 'pending' || m.status === 'transcoding');
  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(() => setPollTick((n) => n + 1), 10000);
    return () => clearInterval(t);
  }, [hasActive]);

  // Load media list
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await getMediaList({ limit: 100, ...(adminToken ? { adminToken } : {}) });
        if (!cancelled) {
          setItems(res.items);
          setError(null);
          setLoading(false);
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load media.';
        setError(message);
        setLoading(false);
        toast.error(message);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [toast, adminToken, refreshKey, pollTick]);

  // Poll transcode jobs for progress when admin token is available
  useEffect(() => {
    // WHY: only poll while something is actually queued/transcoding; an idle
    // admin page makes no background requests.
    if (!adminToken || !hasActive) {
      setJobProgress({});
      return;
    }
    const token = adminToken;
    let cancelled = false;

    async function pollJobs() {
      try {
        const res = await getJobs(token);
        if (cancelled) return;
        const map: Record<string, number> = {};
        for (const job of res.jobs) {
          if (job.status === 'running' || job.status === 'queued') {
            map[job.mediaId] = job.progress;
          }
        }
        setJobProgress(map);
      } catch {
        // WHY: best-effort polling — rate limits or transient errors are
        // swallowed; the badge just won't show a percentage.
      }
    }

    void pollJobs();
    pollRef.current = setInterval(() => void pollJobs(), 15000);

    return () => {
      cancelled = true;
      if (pollRef.current !== null) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [adminToken, hasActive]);

  async function handleDelete(media: MediaSummary) {
    if (!adminToken) {
      toast.error('Admin key required to delete media.');
      return;
    }
    setDeletingId(media.id);
    try {
      await deleteMedia(media.id, adminToken);
      setItems((prev) => prev.filter((m) => m.id !== media.id));
      setConfirmDeleteId(null);
      toast.success(`Deleted "${media.title}".`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete media.';
      toast.error(message);
    } finally {
      setDeletingId(null);
    }
  }

  if (loading) {
    return (
      <div className="state-block">
        <Spinner label="Loading library…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="state-block">
        <p className="state-block__title">Couldn’t load media</p>
        <p>{error}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="state-block">
        <p className="state-block__title">No media yet</p>
        <p>Add a file from “Add from server library” above to get started.</p>
      </div>
    );
  }

  return (
    <div className={styles.catalog}>
      {items.map((media) => {
        const progress = jobProgress[media.id];
        const badge = statusBadge(media.status, progress);
        const ready = media.status === 'ready';
        const transcoding = media.status === 'transcoding';
        const cardClass = `${styles.card} ${ready ? styles.ready : ''} ${selectedId === media.id ? styles.selected : ''}`;
        const handleActivate = ready ? () => onSelect(media) : undefined;
        const isConfirming = confirmDeleteId === media.id;
        const isDeleting = deletingId === media.id;
        return (
          <div
            key={media.id}
            className={cardClass}
            role={ready && !isConfirming ? 'button' : undefined}
            tabIndex={ready && !isConfirming ? 0 : undefined}
            aria-disabled={ready ? undefined : true}
            aria-pressed={ready ? selectedId === media.id : undefined}
            onClick={isConfirming ? undefined : handleActivate}
            onKeyDown={
              ready && !isConfirming
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(media);
                    }
                  }
                : undefined
            }
          >
            <div className={styles.poster}>
              {media.posterUrl ? (
                <img src={media.posterUrl} alt="" loading="lazy" />
              ) : (
                <span className={styles.posterPlaceholder}>No poster</span>
              )}
              <span className={styles.badge}>
                <Badge variant={badge.variant} label={badge.label} />
              </span>
              {transcoding && progress !== undefined ? (
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${Math.round(progress * 100)}%` }}
                  />
                </div>
              ) : null}
              {adminToken ? (
                <button
                  className={styles.deleteBtn}
                  title="Delete media"
                  aria-label={`Delete ${media.title}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDeleteId(media.id);
                  }}
                >
                  ×
                </button>
              ) : null}
              {isConfirming ? (
                <div className={styles.confirmOverlay} onClick={(e) => e.stopPropagation()}>
                  <p className={styles.confirmText}>Delete this media?</p>
                  <div className={styles.confirmActions}>
                    <Button
                      variant="danger"
                      size="sm"
                      loading={isDeleting}
                      onClick={() => void handleDelete(media)}
                    >
                      Delete
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={isDeleting}
                      onClick={() => setConfirmDeleteId(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
            <div className={styles.body}>
              <span className={styles.title} title={media.title}>
                {media.title}
              </span>
              <span className={styles.meta}>{formatDuration(media.durationSec)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
